import { ClientApi } from '../../services/clientApi'
import { NuiCallbacks } from '../../utils/ClientEvents'
import { EFineTuningSettingNames } from './FineTuning.enum'
import { FineTuningProfile, FineTuningSettingsCache, FineTuningSettingsProfileCache, FineTuningVehicle } from './FineTuning.types'

export class FineTuningCache {

	private static instace: FineTuningCache | null = null;

	private intialCache: FineTuningSettingsCache | null = null;

	private constructor() { }

	static getInstance() {
		if (!FineTuningCache.instace) {
			FineTuningCache.instace = new FineTuningCache()
		}
		return FineTuningCache.instace
	}

	public getProfileByName = (name: string, settings: FineTuningSettingsCache | null): FineTuningSettingsProfileCache | undefined => {
		if (!settings) return undefined
		return settings.profiles.find(profile => profile.name.toLowerCase() === name.toLowerCase())
	}

	public initCache = async (vehicleId: number, userSettings: FineTuningVehicle): Promise<FineTuningSettingsCache> => {
		const cache = {
			vehicleId,
			profiles: await Promise.all(userSettings.profiles.map(async profile => ({
				name: profile.name,
				openedCategory: profile.components[0].name,
				aerodynamicProfile: this.getValueFromUserSettings(
					EFineTuningSettingNames.AERODYNAMIC_PROFILE, profile) as number,
				castor: this.getValueFromUserSettings(
					EFineTuningSettingNames.CASTOR, profile) as number,
				downpipe: this.getValueFromUserSettings(
					EFineTuningSettingNames.DOWNPIPE, profile) as number,
				frontCamber: this.getValueFromUserSettings(
					EFineTuningSettingNames.FRONT_CAMBER, profile) as number,
				rearCamber: this.getValueFromUserSettings(
					EFineTuningSettingNames.REAR_CAMBER, profile) as number,
				frontToe: this.getValueFromUserSettings(
					EFineTuningSettingNames.FRONT_TOE, profile) as number,
				rearToe: this.getValueFromUserSettings(
					EFineTuningSettingNames.REAR_TOE, profile) as number,
				clearanceChange: this.getValueFromUserSettings(
					EFineTuningSettingNames.CLEARANCE, profile) as number,
				steeringLock: this.getValueFromUserSettings(
					EFineTuningSettingNames.STEERING_LOCK, profile) as number,
				slipAngle: this.getValueFromUserSettings(
					EFineTuningSettingNames.SLIP_ANGLE, profile) as number,
				frontTrackWidth:
					this.getValueFromUserSettings(EFineTuningSettingNames.FRONT_TRACK_WIDTH, profile),
				rearTrackWidth:
					this.getValueFromUserSettings(EFineTuningSettingNames.REAR_TRACK_WIDTH, profile),
			})))
		}
		const rawCache = JSON.stringify(cache)
		this.intialCache = cache
		localStorage.setItem("fine-tuning-storage", rawCache)
		return cache
	}

	public getCache = (): FineTuningSettingsCache | null => {
		const cache = localStorage.getItem("fine-tuning-storage")
		if (cache) {
			return JSON.parse(cache)
		}
		return null
	}

	public getSaveRequest = (profileName: string): FineTuningSettingsProfileCache | null => {
		const cache = this.getCache()
		if (!cache) return null
		const profile = this.getProfileByName(profileName, cache)
		if (!profile) return null
		return profile
	}

	public updateCache = (settingName: EFineTuningSettingNames, newValue: number, profileName: string): void => {
		const cache = this.getCache()
		if (!cache) return
		const prevValue = this.getValueFromCache(settingName, cache, profileName)
		if (prevValue === newValue) return
		const updatedCache = this.updateValueToCache(settingName, newValue, cache, profileName)
		if (!updatedCache) return
		localStorage.setItem('fine-tuning-storage', JSON.stringify(updatedCache))
	}

	public updateOpenedCategory = (categoryName: string, profileName: string): void => {
		const cache = this.getCache()
		if (!cache) return
		const profile = this.getProfileByName(profileName, cache)
		if (!profile) return
		profile.openedCategory = categoryName
		localStorage.setItem('fine-tuning-storage', JSON.stringify(cache))
	}

	public updateValueToCache = (settingName: EFineTuningSettingNames, value: number, cache: FineTuningSettingsCache | null, profileName: string): FineTuningSettingsCache | null => {
		if (!cache) return null
		const profile = this.getProfileByName(profileName, cache)
		if (!profile) return null

		switch (settingName) {
			case EFineTuningSettingNames.AERODYNAMIC_PROFILE:
				profile.aerodynamicProfile = value
				break
			case EFineTuningSettingNames.CASTOR:
				profile.castor = value
				break
			case EFineTuningSettingNames.DOWNPIPE:
				profile.downpipe = value
				break
			case EFineTuningSettingNames.FRONT_CAMBER:
				profile.frontCamber = value
				break
			case EFineTuningSettingNames.REAR_CAMBER:
				profile.rearCamber = value
				break
			case EFineTuningSettingNames.FRONT_TOE:
				profile.frontToe = value
				break
			case EFineTuningSettingNames.REAR_TOE:
				profile.rearToe = value
				break
			case EFineTuningSettingNames.CLEARANCE:
				profile.clearanceChange = value
				break
			case EFineTuningSettingNames.STEERING_LOCK:
				profile.steeringLock = value
				break
			case EFineTuningSettingNames.SLIP_ANGLE:
				profile.slipAngle = value
				break
			case EFineTuningSettingNames.FRONT_TRACK_WIDTH:
				profile.frontTrackWidth = value
				break
			case EFineTuningSettingNames.REAR_TRACK_WIDTH:
				profile.rearTrackWidth = value
				break
			default:
				break
		}

		this.applyInGameIfNeeded(settingName, value)
		return cache
	}

	public getValueFromCache = (settingName: EFineTuningSettingNames, cache: FineTuningSettingsCache | null, profileName: string): number | null => {
		if (!cache) return null
		const profile = this.getProfileByName(profileName, cache)
		if (!profile) return null
		switch (settingName as EFineTuningSettingNames) {
			case EFineTuningSettingNames.AERODYNAMIC_PROFILE:
				return profile.aerodynamicProfile
			case EFineTuningSettingNames.CASTOR:
				return profile.castor
			case EFineTuningSettingNames.DOWNPIPE:
				return profile.downpipe
			case EFineTuningSettingNames.FRONT_CAMBER:
				return profile.frontCamber
			case EFineTuningSettingNames.REAR_CAMBER:
				return profile.rearCamber
			case EFineTuningSettingNames.FRONT_TOE:
				return profile.frontToe
			case EFineTuningSettingNames.REAR_TOE:
				return profile.rearToe
			case EFineTuningSettingNames.CLEARANCE:
				return profile.clearanceChange
			case EFineTuningSettingNames.STEERING_LOCK:
				return profile.steeringLock
			case EFineTuningSettingNames.SLIP_ANGLE:
				return profile.slipAngle
			case EFineTuningSettingNames.FRONT_TRACK_WIDTH:
				return profile.frontTrackWidth
			case EFineTuningSettingNames.REAR_TRACK_WIDTH:
				return profile.rearTrackWidth
			default:
				return null
		}
	}

	public getValueFromUserSettings = (
		settingName: EFineTuningSettingNames,
		userSettings: FineTuningProfile
	): number => {
		if (!settingName || !userSettings) {
			return 0
		}

		let value = 0
		userSettings.components.forEach(component => {
			component.settingCategories.forEach(category => {
				category.settings.forEach(setting => {
					if (setting.name === settingName) {
						value = setting.currentValue
					}
				})
			})
		})

		return Number(value ? value.toFixed(0) : 0)
	}

	public hasChanges = (profileName: string): boolean => {
		const cache = this.getCache()
		if (!cache || !this.intialCache) return false
		const profile = this.getProfileByName(profileName, cache)
		if (!profile) return false
		const initialProfile = this.getProfileByName(profileName, this.intialCache)
		if (!initialProfile) return false

		return cache.vehicleId !== this.intialCache.vehicleId ||
			profile.aerodynamicProfile !== initialProfile.aerodynamicProfile ||
			profile.castor !== initialProfile.castor ||
			profile.downpipe !== initialProfile.downpipe ||
			profile.frontCamber !== initialProfile.frontCamber ||
			profile.rearCamber !== initialProfile.rearCamber ||
			profile.frontToe !== initialProfile.frontToe ||
			profile.rearToe !== initialProfile.rearToe ||
			profile.clearanceChange !== initialProfile.clearanceChange ||
			profile.steeringLock !== initialProfile.steeringLock ||
			profile.slipAngle !== initialProfile.slipAngle ||
			profile.frontTrackWidth !== initialProfile.frontTrackWidth ||
			profile.rearTrackWidth !== initialProfile.rearTrackWidth
	}

	public updateInitialCache = (newCache: FineTuningSettingsCache) => {
		this.intialCache = newCache
	}

	private readonly HANDLING_THROTTLE_MS = 50; // 20 раз в секунду
	private readonly HANDLING_SKIP_RATIO = 10;

	private handlingThrottleState = new Map<
		EFineTuningSettingNames,
		{
			lastSentAt: number
			burstCount: number
			trailingTimeout?: ReturnType<typeof setTimeout>
			lastValue: number
		}
	>();

	private applyInGameIfNeeded = (settingName: EFineTuningSettingNames, value: number) => {
		const now = Date.now()

		let state = this.handlingThrottleState.get(settingName)
		if (!state) {
			state = {
				lastSentAt: 0,
				burstCount: 0,
				lastValue: value
			}
			this.handlingThrottleState.set(settingName, state)
		}

		state.lastValue = value

		const sendNow = () => {
			state!.lastSentAt = Date.now()
			this.sendHandlingToGame(settingName, state!.lastValue)
		}

		const timeSinceLastSend = now - state.lastSentAt

		if (timeSinceLastSend >= this.HANDLING_THROTTLE_MS) {
			state.burstCount = 0

			if (state.trailingTimeout) {
				clearTimeout(state.trailingTimeout)
				state.trailingTimeout = undefined
			}

			sendNow()
			return
		}

		state.burstCount += 1

		if (state.burstCount % this.HANDLING_SKIP_RATIO === 0) {
			if (state.trailingTimeout) {
				clearTimeout(state.trailingTimeout)
				state.trailingTimeout = undefined
			}

			sendNow()
			return
		}

		if (state.trailingTimeout) {
			clearTimeout(state.trailingTimeout)
		}

		state.trailingTimeout = setTimeout(() => {
			state!.burstCount = 0
			state!.trailingTimeout = undefined
			sendNow()
		}, this.HANDLING_THROTTLE_MS)
	};

	private sendHandlingToGame = (settingName: EFineTuningSettingNames, value: number) => {
		const vehicleGameId = localStorage.getItem('selectedVehicleGameId')
		if (!vehicleGameId) return

		const data = {
			vehicleGameId,
			settingName: '',
			value
		}

		switch (settingName) {
			case EFineTuningSettingNames.CASTOR:
				data.settingName = 'fCastor'
				data.value = value / 100
				break

			case EFineTuningSettingNames.FRONT_CAMBER:
				data.settingName = 'fCamberFront'
				data.value = value / 22.5
				break

			case EFineTuningSettingNames.REAR_CAMBER:
				data.settingName = 'fCamberRear'
				data.value = value / 22.5
				break

			case EFineTuningSettingNames.FRONT_TOE:
				data.settingName = 'fToeFront'
				data.value = value / 45
				break

			case EFineTuningSettingNames.REAR_TOE:
				data.settingName = 'fToeRear'
				data.value = value / 45
				break

			case EFineTuningSettingNames.CLEARANCE:
				data.settingName = 'suspensionHeight'
				data.value = value / -100
				break

			case EFineTuningSettingNames.STEERING_LOCK:
				data.settingName = 'fSteeringLock'
				data.value = value / 45
				break

			case EFineTuningSettingNames.FRONT_TRACK_WIDTH:
				console.log(`Setting value ${value}`)
				ClientApi.send(NuiCallbacks.SET_HANDLING, {
					vehicleGameId,
					settingName: 'wheeltrackwidth0',
					value
				})
				ClientApi.send(NuiCallbacks.SET_HANDLING, {
					vehicleGameId,
					settingName: 'wheeltrackwidth1',
					value
				})
				return

			case EFineTuningSettingNames.REAR_TRACK_WIDTH:
				ClientApi.send(NuiCallbacks.SET_HANDLING, {
					vehicleGameId,
					settingName: 'wheeltrackwidth2',
					value
				})
				ClientApi.send(NuiCallbacks.SET_HANDLING, {
					vehicleGameId,
					settingName: 'wheeltrackwidth3',
					value
				})
				return
		}

		if (data.settingName) {
			ClientApi.send(NuiCallbacks.SET_HANDLING, data)
		}
	};
}